/**
 * Navigation Component
 * Main navigation header with links to different sections
 */

import React, { useState, useRef, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'
import { Sun, Moon, ChevronDown, User, LogOut, HelpCircle } from 'lucide-react'
import UniversalSearchButton from './UniversalSearchButton'
import clsx from 'clsx'

const Navigation = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef(null)
  const [isInProgressOpen, setIsInProgressOpen] = useState(false)
  const inProgressRef = useRef(null)

  // Close the In Progress dropdown when clicking outside it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inProgressRef.current && !inProgressRef.current.contains(event.target)) {
        setIsInProgressOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const { theme, toggleTheme } = useTheme()

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const navItems = [
    { path: '/', label: 'Dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
    { path: '/projects', label: 'Projects', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10' },
    { path: '/jobs', label: 'Jobs', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01' },
    { path: '/help', label: 'Help', icon: 'M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z', external: true },
  ]

  // Pages that are live but whose numbers we are still checking with Astley. They sit
  // under one "In Progress" heading so users know to treat them with care. The Insights
  // reports are cross-cutting, not tied to one job/project — grouped the same way
  // TherionOS groups them, so the mental model carries over for anyone who knows it.
  const inProgressSections = [
    { items: [
      { path: '/order-book', label: 'Order Book', icon: 'M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z' },
    ] },
    { heading: 'Insights', items: [
      { path: '/plan-vs-actual', label: 'Plan vs Actual', icon: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6' },
      { path: '/labour-analysis', label: 'Labour Analysis', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
      { path: '/stock-age', label: 'Stock Age', icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4' },
      { path: '/wip', label: 'WIP & Job Performance', icon: 'M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2' },
    ] },
  ]
  const inProgressNote = 'We are still checking the numbers on these pages.'

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(path)
  }

  const isInProgressActive = inProgressSections.some((section) => section.items.some((item) => isActive(item.path)))

  return (
    <>
    <nav className="sticky top-0 z-30 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 shadow-sm transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo and Main Nav */}
          <div className="flex">
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center text-white text-sm font-semibold">
                  <i className="fas fa-brain text-white text-sm"></i>
                </div>
                <span className="text-xl font-bold text-slate-900 dark:text-white">Cortex Operations</span>
              </Link>
            </div>

            {/* Navigation Links */}
            <div className="hidden sm:ml-8 sm:flex sm:space-x-4 sm:items-center">
              {navItems.map((item) => {
                const linkClasses = clsx(
                  'inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                  isActive(item.path)
                    ? 'text-blue-600 bg-blue-50 dark:text-blue-300 dark:bg-blue-500/10'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-800'
                )

                const iconSvg = (
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d={item.icon}
                    />
                  </svg>
                )

                // The In Progress pages follow Jobs, in their own dropdown rather than
                // sitting as top-level items.
                const inProgressDropdown = item.path === '/jobs' && (
                  <div className="relative" ref={inProgressRef} key="in-progress">
                    <button
                      type="button"
                      onClick={() => setIsInProgressOpen(!isInProgressOpen)}
                      className={clsx(
                        'inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                        isInProgressActive
                          ? 'text-blue-600 bg-blue-50 dark:text-blue-300 dark:bg-blue-500/10'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-800'
                      )}
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2v-6" />
                      </svg>
                      In Progress
                      <ChevronDown className={clsx('w-3.5 h-3.5 ml-1 transition-transform', isInProgressOpen && 'rotate-180')} />
                    </button>

                    {isInProgressOpen && (
                      <div className="absolute left-0 mt-2 w-56 rounded-lg bg-white dark:bg-slate-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 py-1">
                        <p className="px-4 py-2 text-xs text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                          {inProgressNote}
                        </p>
                        {inProgressSections.map((section) => (
                          <React.Fragment key={section.heading || 'main'}>
                            {section.heading && (
                              <p className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                                {section.heading}
                              </p>
                            )}
                            {section.items.map((page) => (
                              <Link
                                key={page.path}
                                to={page.path}
                                onClick={() => setIsInProgressOpen(false)}
                                className={clsx(
                                  'flex items-center px-4 py-2 text-sm transition-colors',
                                  isActive(page.path)
                                    ? 'text-blue-600 bg-blue-50 dark:text-blue-300 dark:bg-blue-500/10'
                                    : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700'
                                )}
                              >
                                <svg className="w-4 h-4 mr-2 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={page.icon} />
                                </svg>
                                {page.label}
                              </Link>
                            ))}
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                  </div>
                )

                // External link opens in new window
                if (item.external) {
                  return (
                    <a
                      key={item.path}
                      href={item.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={linkClasses}
                    >
                      {iconSvg}
                      {item.label}
                    </a>
                  )
                }

                // Internal link uses React Router
                return (
                  <React.Fragment key={item.path}>
                    <Link
                      to={item.path}
                      className={linkClasses}
                    >
                      {iconSvg}
                      {item.label}
                    </Link>
                    {inProgressDropdown}
                  </React.Fragment>
                )
              })}
            </div>
          </div>

          {/* Right side controls */}
          <div className="flex items-center space-x-3">
            {/* Universal Search Button */}
            <UniversalSearchButton
              onSearch={(query, context) => {
                // Search is handled through context, no additional action needed here
                console.log(`Searching for "${query}" in ${context}`)
              }}
            />

            {/* User Profile Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center space-x-2 px-2 py-2 text-sm rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-medium">
                    {user?.email?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
                <ChevronDown className={clsx(
                  "w-4 h-4 text-slate-500 dark:text-slate-400 transition-transform",
                  isDropdownOpen && "transform rotate-180"
                )} />
              </button>

              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-56 rounded-lg bg-white dark:bg-slate-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                  <div className="py-1">
                    {/* User Info */}
                    <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
                      <p className="text-sm font-medium text-slate-900 dark:text-white">Signed in as</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400 truncate">{user?.email || 'User'}</p>
                    </div>

                    {/* Profile Link */}
                    {/* 
                    <button
                      className="w-full flex items-center px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                      onClick={() => {
                        setIsDropdownOpen(false)
                        // navigate('/profile') // Uncomment when profile page is ready
                      }}
                    >
                      <User className="w-4 h-4 mr-3" />
                      Profile
                    </button>
                    */}

                    {/* Theme Toggle */}
                    <button
                      className="w-full flex items-center px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                      onClick={() => {
                        toggleTheme()
                      }}
                    >
                      {theme === 'dark' ? (
                        <>
                          <Sun className="w-4 h-4 mr-3" />
                          Light mode
                        </>
                      ) : (
                        <>
                          <Moon className="w-4 h-4 mr-3" />
                          Dark mode
                        </>
                      )}
                    </button>

                    {/* Divider */}
                    <div className="border-t border-slate-200 dark:border-slate-700"></div>

                    {/* Logout */}
                    <button
                      onClick={() => {
                        setIsDropdownOpen(false)
                        handleLogout()
                      }}
                      className="w-full flex items-center px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                    >
                      <LogOut className="w-4 h-4 mr-3" />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div className="sm:hidden">
        <div className="pt-2 pb-3 space-y-1">
          {navItems.map((item) => {
            const mobileClasses = clsx(
              'block pl-3 pr-4 py-2 text-base font-medium transition-colors',
              isActive(item.path)
                ? 'text-blue-600 bg-blue-50 border-l-4 border-blue-600 dark:text-blue-300 dark:bg-blue-500/10'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-800'
            )

            // External link opens in new window
            if (item.external) {
              return (
                <a
                  key={item.path}
                  href={item.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={mobileClasses}
                >
                  {item.label}
                </a>
              )
            }

            // Internal link uses React Router, with the In Progress pages listed as an
            // indented sub-section right after Jobs — same grouping as desktop, shown
            // flat instead of behind a dropdown since mobile menus scroll.
            return (
              <React.Fragment key={item.path}>
                <Link
                  to={item.path}
                  className={mobileClasses}
                >
                  {item.label}
                </Link>
                {item.path === '/jobs' && (
                  <>
                    <p className="pl-3 pr-4 pt-3 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                      In Progress
                    </p>
                    <p className="pl-3 pr-4 pb-1 text-xs text-slate-500 dark:text-slate-400">{inProgressNote}</p>
                    {inProgressSections.flatMap((section) => section.items).map((page) => (
                      <Link
                        key={page.path}
                        to={page.path}
                        className={clsx(
                          'block pl-6 pr-4 py-2 text-base font-medium transition-colors',
                          isActive(page.path)
                            ? 'text-blue-600 bg-blue-50 border-l-4 border-blue-600 dark:text-blue-300 dark:bg-blue-500/10'
                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-800'
                        )}
                      >
                        {page.label}
                      </Link>
                    ))}
                  </>
                )}
              </React.Fragment>
            )
          })}
        </div>
      </div>
    </nav>
    </>
  )
}

export default Navigation
